import type { Move } from '../../../schemas/move';

/**
 * Mosse avanzate del Paladino L6-10 (manuale V1, rr. 5050-5128). 14 mosse.
 *
 * Pattern notabili:
 * - **`MoveSchema.replaces` 3 casi consecutivi nella stessa classe**:
 *    - *Attacco Coordinato* sostituisce *Attacco a Seguire*
 *    - *Difensore Inattaccabile* sostituisce *Difensore Devoto*
 *    - *Sempre Avanti!* sostituisce *Carica!*
 *   Quarto/quinto/sesto uso strutturale del pattern (dopo Mago/FI-21,
 *   Monaco/2 casi, Ranger/1).
 * - **3 mosse richiedono *Missione*** (Protezione Sacra, Castigo, Perfetto
 *   Cavaliere): pattern di sub-classe basato sulla mossa-config.
 * - **3 mosse richiedono *Aura***: Aura Potente, Nova, Maestro Ospitaliere.
 * - ***Templare*** (multiclasse): analogo a *Crociato* di L2-5 (FI-17).
 * - ***Comandante Supremo*** (FI-8 stub): gregari description-only.
 * - ***Nato in Sella*** (FI-8 al limite): cavalcatura description-only.
 */

const consapevolezzaUltraterrena: Move = {
  id: 'paladino.consapevolezza-ultraterrena',
  name: 'Consapevolezza Ultraterrena',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger:
    'Quando assisti al manifestarsi di una magia ultraterrena (divina, fatata, demoniaca, ecc.)',
  description:
    'Se chiedi al GM che effetti ha e da quale entità proviene, ottieni una risposta sincera. La prossima volta che sfrutti a tuo vantaggio queste informazioni prendi +1 al tiro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const protezioneSacra: Move = {
  id: 'paladino.protezione-sacra',
  name: 'Protezione Sacra',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando stai perseguendo attivamente gli obiettivi della tua *Missione*',
  description: 'Hai +1 Armatura.',
  tags: [],
  prerequisites: ['paladino.missione'],
  replaces: [],
};

const castigo: Move = {
  id: 'paladino.castigo',
  name: 'Castigo',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando stai perseguendo attivamente gli obiettivi della tua *Missione*',
  description: 'Infliggi +1d4 danni.',
  tags: [],
  prerequisites: ['paladino.missione'],
  replaces: [],
};

const perfettoCavaliere: Move = {
  id: 'paladino.perfetto-cavaliere',
  name: 'Perfetto Cavaliere',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando inizi una nuova *Missione*',
  description: 'Scegli 3 benedizioni invece di 2.',
  tags: [],
  prerequisites: ['paladino.missione'],
  replaces: [],
};

const attaccoCoordinato: Move = {
  id: 'paladino.attacco-coordinato',
  name: 'Attacco Coordinato',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando *tagli e spacchi*',
  description:
    'Scegli un alleato. Egli ottiene +1 al tiro per il suo prossimo attacco contro il tuo bersaglio e, se va a segno, infligge +1d4 danni.',
  tags: [],
  prerequisites: [],
  replaces: ['paladino.attacco-a-seguire'],
};

const difensoreInattaccabile: Move = {
  id: 'paladino.difensore-inattaccabile',
  name: 'Difensore Inattaccabile',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando *difendi*',
  description:
    "Ottieni sempre +1 presa, anche con 6−. Inoltre, se scegli l'opzione di infliggere danno, infliggi +1d4 danni al nemico.",
  tags: [],
  prerequisites: [],
  replaces: ['paladino.difensore-devoto'],
};

const irriducibile: Move = {
  id: 'paladino.irriducibile',
  name: 'Irriducibile',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando subisci una Debilitazione (anche da *Egida Insanguinata*)',
  description: "Prendi +1 al prossimo tiro contro qualsiasi cosa l'abbia causata.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const auraPotente: Move = {
  id: 'paladino.aura-potente',
  name: 'Aura Potente',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando usi la mossa *Aura*',
  description: 'Con 12+ prendi 4. Con 10-11, ottieni 3 prese. Con 7-9, prendi 2.',
  tags: [],
  prerequisites: ['paladino.aura'],
  replaces: [],
};

const nova: Move = {
  id: 'paladino.nova',
  name: 'Nova',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando spendi prese di *Aura* (effetto opzionale)',
  description:
    "Puoi spendere tutte le tue prese residue di *Aura* (minimo 2) per applicare l'effetto di uno degli scopi che hai scelto a tutti i bersagli validi entro distanza *lunga* anziché a uno solo. Per ogni presa oltre la seconda che spendi in questo modo applichi un ulteriore effetto a tua scelta (anche lo stesso più volte) tra quelli degli scopi selezionati.",
  tags: [],
  prerequisites: ['paladino.aura'],
  replaces: [],
};

const maestroOspitaliere: Move = {
  id: 'paladino.maestro-ospitaliere',
  name: 'Maestro Ospitaliere',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando usi *Aura* per curare PF, oppure usi *Imposizione delle Mani*',
  description:
    "Ora l'opzione di cura di *Aura* ti consente di guarire il bersaglio di 1d8 PF per ogni presa, e, se spendi 2 prese, anche le emorragie vengono arrestate. Con *Imposizione delle Mani*, invece, qualsiasi cosa tu decida di guarire, curi il bersaglio di +1d8 PF anziché di +1d4 PF.",
  tags: [],
  // Manuale r. 5098: "Richiede: *Ospitaliere* e (*Aura* o *Imposizione delle
  // Mani*)". Pattern: 1 mossa fissa + 1 alternativa tra 2.
  prerequisites: [
    'paladino.ospitaliere',
    {
      anyOf: ['paladino.aura', 'paladino.imposizione-delle-mani'],
    },
  ],
  replaces: [],
};

const templare: Move = {
  id: 'paladino.templare',
  name: 'Templare',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando prendi questa mossa al level-up',
  description:
    'Scegli una mossa non multiclasse tra quelle del Guerriero, del Chierico (solo se hai la mossa *Ordine Sacro*), oppure del Warlock (solo se hai la mossa *Ordine Sovrannaturale*).\n\n**NB**: con archetipo Elfo, puoi sempre scegliere anche tra le mosse del Druido.',
  tags: ['multiclasse'],
  prerequisites: [],
  replaces: [],
  // FI-37 + FI-43 (2026-05-15, manuale V1 rr. 5103-5107 + r. 4847): identico a
  // *Crociato* ma per L6-10.
  multiclass: {
    picks: { kind: 'fixed-classes', classes: ['guerriero'] },
    extendsByArchetype: [{ archetypeId: 'elfo', extraClasses: ['druido'] }],
    extendsByMove: [
      { moveId: 'paladino.ordine-sacro', extraClasses: ['chierico'] },
      { moveId: 'paladino.ordine-sovrannaturale', extraClasses: ['warlock'] },
    ],
  },
};

const sempreAvanti: Move = {
  id: 'paladino.sempre-avanti',
  name: 'Sempre Avanti!',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando guidi la carica contro il nemico',
  description:
    'Coloro che ti seguono prendono +1 al prossimo tiro e +2 Armatura la prossima volta che subiscono danni.',
  tags: [],
  prerequisites: [],
  replaces: ['paladino.carica'],
};

// FI-8 stub: gregari description-only finché il sistema Seguaci a Ranghi 1-3
// non sarà strutturato (PR FI-8 post-12 classi).
const comandanteSupremo: Move = {
  id: 'paladino.comandante-supremo',
  name: 'Comandante Supremo',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger:
    'Quando ti *accampi* e arringhi i tuoi gregari; quando un tuo gregario usa una sua Abilità',
  description:
    "Quando **ti *accampi* e arringhi i tuoi gregari**, perdi tutte le prese residue e tira+**CAR**. Con 10+, prendi 3. Con 7-9, prendi 2. Con 6-, prendi comunque 1, ma il giorno successivo i tuoi gregari avranno un -1 continuato a tutti i tiri.\n\nQuando **un tuo gregario usa una sua Abilità**, puoi spendere una presa per fare in modo che essa abbia un effetto straordinario (a discrezione del GM), che il gregario non subisca conseguenze, o che l'utilizzo dell'Abilità non venga inibito (a tua scelta).",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Prendi 3.' },
    on7to9: { text: 'Prendi 2.' },
    on6minus: { text: 'Prendi 1, ma i tuoi gregari hanno -1 continuato il giorno successivo.' },
  },
  tags: [],
  prerequisites: ['paladino.la-voce-dell-autorita'],
  replaces: [],
};

// FI-8 stub: la cavalcatura è description-only (sistema Seguaci non ancora
// strutturato, PR FI-8 post-12 classi).
const natoInSella: Move = {
  id: 'paladino.nato-in-sella',
  name: 'Nato in Sella',
  type: 'advanced_6_10',
  classId: 'paladino',
  trigger: 'Quando ti lanci al galoppo',
  description:
    "Tira+**CAR**. Con 10+, scegli 2 dei seguenti effetti. Con 7-9, scegline 1 solo:\n\n- la tua cavalcatura butta a terra o scalcia i nemici che si trovano sulla tua strada;\n- issi un alleato in sella dietro di te;\n- la tua cavalcatura esegue un salto o un'altra manovra d'effetto, portandoti fuori dalla mischia o facendoti superare un ostacolo;\n- cavalchi veloce come il vento: puoi arrivare dove vuoi molto rapidamente e, se qualcuno t'insegue, non potrà raggiungerti tanto in fretta.",
  roll: {
    stat: 'carisma',
    on10plus: { text: 'Scegli 2 effetti dalla lista.' },
    on7to9: { text: 'Scegli 1 effetto dalla lista.' },
  },
  tags: [],
  prerequisites: ['paladino.richiama-cavalcatura'],
  replaces: [],
};

export const paladinoAdvanced6to10Moves: readonly Move[] = [
  consapevolezzaUltraterrena,
  protezioneSacra,
  castigo,
  perfettoCavaliere,
  attaccoCoordinato,
  difensoreInattaccabile,
  irriducibile,
  auraPotente,
  nova,
  maestroOspitaliere,
  templare,
  sempreAvanti,
  comandanteSupremo,
  natoInSella,
] as const;
